/**
 * THE domain icon registry — one glyph per real-estate concept, sitewide.
 *
 * Ends the era of four coexisting icon systems (Remix classes, Phosphor CDN,
 * lucide imports, ad-hoc SVGs) disagreeing about what a bathroom or a villa
 * looks like. Every domain concept (property types, metrics, amenities,
 * utilities) resolves HERE, keyed by the SAME ids the option configs use
 * (propertyFormOptions AMENITIES_LIST/UTILITIES_LIST ids, propertyTypeCatalog
 * ids, metric names), so config files carry ids — never icon class strings.
 *
 * Glyph sources, in one consistent 24×24 stroke style:
 *  - lucide-react (already a dependency, ISC) for everything it draws well;
 *  - hand-drawn customs below (same viewBox/stroke contract) for concepts
 *    lucide lacks: elevator, basement stairs, townhouse, duplex, penthouse,
 *    commercial building, price-per-m².
 *
 * Generic UI chrome (arrows, close, loaders, chevrons) intentionally stays
 * Remix — this registry is domain vocabulary only.
 *
 * Browse everything at /design/icons.
 */
import type { ComponentType, ReactNode, SVGProps } from 'react';
import {
  Accessibility, Armchair, Bath, BatteryCharging, BedDouble, BedSingle,
  BellRing, BookOpen, Briefcase, BrushCleaning, Building, Building2, CarFront,
  Cctv, CircleCheck, CloudRainWind, CookingPot, DoorOpen, Droplet, Dumbbell,
  Factory, Fence, Flame, FlameKindling, Frame, Fuel, Grid2x2, Home, Hospital,
  Hotel, HouseWifi, LandPlot, Layers, Package, PawPrint, Phone, PhoneCall,
  Plug, Presentation, Router, School, Scaling, Shirt, ShieldCheck,
  ShoppingBag, Siren, Snowflake, Sofa, SquareParking, Store, Sun, TentTree,
  ThermometerSnowflake, ThermometerSun, Tractor, Trash2, TreePalm, Trees,
  UserCheck, Volleyball, VolumeX, Warehouse, WashingMachine, Waves,
  WavesLadder, Wifi, Wrench, Zap,
} from 'lucide-react';

export interface DomainGlyphProps extends SVGProps<SVGSVGElement> {
  size?: number | string;
  strokeWidth?: number | string;
}
export type DomainGlyph = ComponentType<DomainGlyphProps>;

/** Wrap raw paths in the same 24×24 stroke contract lucide uses. */
function custom(paths: ReactNode): DomainGlyph {
  return function CustomGlyph({ size = 24, strokeWidth = 2, ...rest }: DomainGlyphProps) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width={size}
        height={size}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        {...rest}
      >
        {paths}
      </svg>
    );
  };
}

// -- Customs (lucide has no faithful glyph) ---------------------------------

/** Elevator: cabin with door split + call arrows. */
const ElevatorGlyph = custom(
  <>
    <rect x="4.5" y="3" width="15" height="18" rx="2" />
    <path d="M12 7.5v13.5" />
    <path d="M6.9 12.2 8.4 10.4l1.5 1.8" />
    <path d="M14.1 11.8l1.5 1.8 1.5-1.8" />
  </>,
);

/** Basement: stairs descending with a down arrow. */
const BasementGlyph = custom(
  <>
    <path d="M3 9h4.5v4H12v4h4.5v4H21" />
    <path d="M17.5 3v6" />
    <path d="m15 6.5 2.5 2.5L20 6.5" />
  </>,
);

/** Townhouse: two attached gabled houses in a row. */
const TownhouseGlyph = custom(
  <>
    <path d="M3 21v-9.5L7.5 7l4.5 4.5V21" />
    <path d="M12 11.5 16.5 7 21 11.5V21" />
    <path d="M3 21h18" />
    <path d="M6.5 21v-4h2v4" />
    <path d="M15.5 21v-4h2v4" />
  </>,
);

/** Duplex: one house, two stacked units (mid-floor divider + door). */
const DuplexGlyph = custom(
  <>
    <path d="m4 11 8-7 8 7" />
    <path d="M6 9.5V21h12V9.5" />
    <path d="M6 15h12" />
    <path d="M11 21v-3.5h2V21" />
  </>,
);

/** Penthouse: tower with an emphasized rooftop unit. */
const PenthouseGlyph = custom(
  <>
    <path d="M6 21V9h12v12" />
    <path d="M9 9V4.5h6V9" />
    <path d="M12 4.5V2.5" />
    <path d="M9.5 13h1.5M13 13h1.5M9.5 17h1.5M13 17h1.5" />
    <path d="M4 21h16" />
  </>,
);

/** Commercial building: floors above an awninged storefront. */
const CommercialBuildingGlyph = custom(
  <>
    <path d="M5 21V9h14v12" />
    <path d="M4 9l1.5-4h13L20 9" />
    <path d="M4 9h16" />
    <path d="M8.5 13h1.5M14 13h1.5" />
    <path d="M10 21v-4.5h4V21" />
    <path d="M3.5 21h17" />
  </>,
);

/** Price per m²: unit grid with a price tag corner. */
const PricePerSqmGlyph = custom(
  <>
    <rect x="3.5" y="3.5" width="17" height="17" rx="2" />
    <path d="M12 3.5v17M3.5 12h17" />
    <circle cx="16.25" cy="7.75" r="2.35" />
  </>,
);

// -- The registry -----------------------------------------------------------

export const DOMAIN_ICONS = {
  // Metrics
  bedroom: BedDouble,
  bathroom: Bath, // bathtub — the water utility is a droplet; never share glyphs
  area: Scaling,
  parking: SquareParking,
  floors: Layers,
  'price-sqm': PricePerSqmGlyph,

  // Property types (ids = propertyTypeCatalog ids; *_land variants share 'land')
  apartment: Building2,
  villa: Home,
  floor: Layers,
  building: Building,
  land: LandPlot,
  rest_house: TreePalm,
  chalet: TentTree,
  room: DoorOpen,
  townhouse: TownhouseGlyph,
  studio: Sofa,
  penthouse: PenthouseGlyph,
  duplex: DuplexGlyph,
  office: Briefcase,
  commercial_building: CommercialBuildingGlyph,
  warehouse: Warehouse,
  shop: Store,
  showroom: Presentation,
  farm: Tractor,
  mall: ShoppingBag,
  hotel: Hotel,
  workshop: Wrench,
  factory: Factory,
  school: School,
  health_center: Hospital,
  station: Fuel,

  // Amenities (ids = AMENITIES_LIST ids)
  central_ac: Snowflake,
  smart_home: HouseWifi,
  maid_room: BrushCleaning,
  driver_room: CarFront,
  guest_house: BedSingle,
  storage: Package,
  basement: BasementGlyph,
  elevator: ElevatorGlyph,
  intercom: PhoneCall,
  concierge: BellRing,
  security: ShieldCheck,
  balcony: Fence,
  roof: Sun,
  garden: Trees,
  courtyard: Frame,
  pool: WavesLadder,
  gym: Dumbbell,
  playground: Volleyball,
  pet_friendly: PawPrint,

  // Legacy amenity concepts still stored as free text in older DB rows
  furnished: Sofa,
  kitchen: CookingPot,
  laundry: WashingMachine,
  walk_in_closet: Shirt,
  private_entrance: DoorOpen,
  study_room: BookOpen,
  fireplace: FlameKindling,
  sauna: ThermometerSun,
  jacuzzi: Bath,
  lobby: Armchair,
  cctv: Cctv,
  guard: UserCheck,
  fire_alarm: Siren,
  generator: BatteryCharging,
  double_glazing: Grid2x2,
  soundproofing: VolumeX,
  wheelchair: Accessibility,

  // Generic fallbacks (mirror the old ph-check-circle / ri-plug-line defaults)
  amenity: CircleCheck,
  utility: Plug,

  // Utilities (ids = UTILITIES_LIST ids)
  water: Droplet,
  electricity: Zap,
  sewage: Waves,
  gas: Flame,
  phone: Phone,
  internet: Wifi,
  fiber: Router,
  flood_drainage: CloudRainWind,
  trash_collection: Trash2,
  district_cooling: ThermometerSnowflake,
} as const satisfies Record<string, DomainGlyph>;

export type DomainIconId = keyof typeof DOMAIN_ICONS;

export const DOMAIN_ICON_IDS = Object.keys(DOMAIN_ICONS) as DomainIconId[];

export function isDomainIconId(id: string): id is DomainIconId {
  return id in DOMAIN_ICONS;
}
